import { useEffect, useState } from 'react';

// Estilos
import styles from './itemlist.module.css';

// Assets
import { FaEdit, FaTrash, FaRocket } from 'react-icons/fa';
import { IoMdAdd } from "react-icons/io";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

function ItemList({
    items = [],
    onEdit,
    onDelete,
    onClickItem,
    onAdd,
    toggleStatus,
    statusFiltro = 1,
    /** Opcional: se enviada, habilita o botão "Publicar" nos itens cujo status !== "published" */
    onPublish,
}) {
    const [switchChecked, setSwitchChecked] = useState(Number(statusFiltro) === 1);
    const [busca, setBusca] = useState('');
    const [itensFiltrados, setItensFiltrados] = useState(items);

    const [paginaAtual, setPaginaAtual] = useState(1);
    const [itensPorPagina, setItensPorPagina] = useState(20);

    // loading por item durante publicação
    const [publishingId, setPublishingId] = useState(null);

    useEffect(() => {
        setSwitchChecked(Number(statusFiltro) === 1);
    }, [statusFiltro]);

    useEffect(() => {
        if (busca.length >= 4) {
            const termo = busca.toLowerCase();
            const filtrados = items.filter((item) =>
                item.name?.toLowerCase().includes(termo)
            );
            setItensFiltrados(filtrados);
        } else {
            setItensFiltrados(items);
        }
        setPaginaAtual(1);
    }, [busca, items]);

    const totalPaginas = Math.ceil(itensFiltrados.length / itensPorPagina);
    const indexInicial = (paginaAtual - 1) * itensPorPagina;
    const indexFinal = indexInicial + itensPorPagina;
    const itensPaginados = itensFiltrados.slice(indexInicial, indexFinal);

    const handleSwitchToggle = () => {
        if (typeof toggleStatus === 'function') toggleStatus();
    };

    const handleChangeItensPorPagina = (e) => {
        setItensPorPagina(Number(e.target.value));
        setPaginaAtual(1);
    };

    const handlePublish = async (item) => {
        if (typeof onPublish !== 'function') return;
        try {
            setPublishingId(item.id);
            await onPublish(item); // pai controla toasts/erros/refresh
        } finally {
            setPublishingId(null);
        }
    };

    return (
        <div className={styles.listWrapper}>
            <div className={styles.topBar}>
                <div className={styles.searchAndToggle}>
                    {typeof toggleStatus === 'function' && (
                        <div className={styles.switchWrapper}>
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    checked={switchChecked}
                                    onChange={handleSwitchToggle}
                                />
                                <span className={styles.slider}></span>
                            </label>
                            <span className={styles.switchLabel}>
                                {switchChecked ? 'Turma Ativa' : 'Turma Inativa'}
                            </span>
                        </div>
                    )}

                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Buscar por nome"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                    />
                </div>

                <select
                    className={styles.pageSizeSelect}
                    value={itensPorPagina}
                    onChange={handleChangeItensPorPagina}
                >
                    {[20, 50, 100, 200].map((qtd) => (
                        <option key={qtd} value={qtd}>
                            {qtd} por página
                        </option>
                    ))}
                </select>

                <button className={styles.addButton} onClick={onAdd}>
                    <IoMdAdd /> Adicionar
                </button>
            </div>

            {itensPaginados.length === 0 ? (
                <div className={styles.containerEmptyMessage}>
                    <p className={styles.emptyMessageTop}>Nada encontrado.</p>
                    <p className={styles.emptyMessageBottom}>Tente outro termo ou cadastre algo novo.</p>
                </div>
            ) : (
                <>
                    <div className={styles.listContainer}>
                        {itensPaginados.map((item) => {
                            // Regra pedida: se onPublish existir e status !== "published", mostrar botão
                            const canShowPublishBtn =
                                typeof onPublish === 'function' &&
                                String(item?.status ?? '').toLowerCase() !== 'published';

                            const isPublishing = publishingId === item.id;

                            return (
                                <div key={item.id} className={styles.listItem}>
                                    <span
                                        className={styles.itemName}
                                        onClick={() => onClickItem?.(item)}
                                        title="Visualizar"
                                    >
                                        {item.name}
                                    </span>

                                    {canShowPublishBtn && (
                                        <button
                                            className={styles.iconBtn}
                                            onClick={() => handlePublish(item)}
                                            title="Publicar agora"
                                            disabled={isPublishing}
                                            style={{ marginLeft: 8 }}
                                        >
                                            <FaRocket />
                                            <span style={{ marginLeft: 6 }}>
                                                {isPublishing ? 'Publicando...' : 'Publicar'}
                                            </span>
                                        </button>
                                    )}

                                    <div className={styles.rightContainer}>
                                        {/* IMPORTANTE: quando onPublish existir, NÃO mostramos o badge de status (nem o dot) */}
                                        {typeof onPublish !== 'function' && typeof item.status !== 'undefined' && (
                                            <span
                                                className={`${styles.statusBadge} ${
                                                    Number(item.status) === 1 ? styles.active : styles.inactive
                                                }`}
                                            >
                                                <span className={styles.statusDot}></span>
                                                {/* Mantido como estava para outros usos */}
                                                <span>
                                                    {Number(item.status) === 1 ? 'Ativa' : 'Inativa'}
                                                </span>
                                            </span>
                                        )}

                                        <div className={styles.actions}>
                                            <button
                                                className={styles.iconBtn}
                                                onClick={() => onEdit?.(item)}
                                                title="Editar"
                                            >
                                                <FaEdit />
                                            </button>

                                            <button
                                                className={styles.iconBtn}
                                                onClick={() => onDelete?.(item)}
                                                title="Excluir"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.paginationControls}>
                        <button
                            onClick={() => setPaginaAtual(p => Math.max(p - 1, 1))}
                            disabled={paginaAtual === 1}
                        >
                            <FaChevronLeft />
                        </button>

                        <span>
                            Página {paginaAtual} de {totalPaginas}
                        </span>

                        <button
                            onClick={() => setPaginaAtual(p => Math.min(p + 1, totalPaginas))}
                            disabled={paginaAtual === totalPaginas}
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default ItemList;
